import type { Uuid } from "spacetimedb";
import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon, LayersIcon, LayoutGridIcon } from "lucide-react";
import { motion } from "motion/react";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { match } from "ts-pattern";

import { useUser } from "@/web/auth/useUser";
import { Redirect } from "@/web/components/Redirect";
import { Button } from "@/web/components/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/web/components/primitives/select";
import {
  Timeline,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/web/components/primitives/timeline";
import { ToggleGroup, ToggleGroupItem } from "@/web/components/primitives/toggle-group";
import { DefaultNavbar } from "@/web/components/styled/AppNavbar";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { resetDocumentViewAfterSpaFormNavigation } from "@/web/lib/documentViewReset";
import { useDrawerSmUp } from "@/web/lib/drawerLayout";
import { env } from "@/web/lib/env";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { cn } from "@/web/lib/styling";
import { AcknowledgmentModal } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/AcknowledgmentModal";
import { AcknowledgmentsProvider } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/AcknowledgmentsProvider";
import { MESSAGE_KEYS } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/messageKeys";
import { OrientationTour } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/OrientationTour";
import type { ActionFilterCategory } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/tourSteps";
import { useTourStep } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/useTourStep";
import { BrandSwitcher } from "@/web/routes/_protected/smb/-dashboard/-BrandSwitcher";
import { CardDeck } from "@/web/routes/_protected/smb/-dashboard/-CardDeck";
import { cardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-engine";
import {
  brandIndexSearch,
  BrandSelectionProvider,
  useSelectedBrandId,
} from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import {
  useSuspendedCardEngineInputs,
  useSuspendedSmbSuperViewRows,
  useSuspendedUnderwritableEntities,
} from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { BudgetTileButton } from "@/web/routes/_protected/smb/-dashboard/-footer/BudgetTile";
import { FundraiseTileButton } from "@/web/routes/_protected/smb/-dashboard/-footer/FundraiseTile";
import { NegotiateTileButton } from "@/web/routes/_protected/smb/-dashboard/-footer/NegotiateTile";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";
import { SmbActionCategories } from "@/web/routes/_protected/smb/-dashboard/-types";
import { useCardDeck } from "@/web/routes/_protected/smb/-dashboard/-useCardDeck";

export const Route = createFileRoute("/_protected/profile/smb")({
  component: SmbDashboard,
  // `?brand=N` scopes the dashboard to the Nth of the user's brands (absent =
  // the first). Shared with the TTW composer so the selection survives the
  // navigation between them — see `brandSelection.tsx`.
  validateSearch: brandIndexSearch,
});

const FILTERABLE_CATEGORIES: readonly ActionFilterCategory[] = [
  "all",
  ...SmbActionCategories.filter((c) => c !== "generic"),
];

const humanCardCategory = (a: ActionFilterCategory) =>
  match(a)
    .with("all", () => "All cards")
    .with("fundraise", () => "Fund")
    .with("budget", () => "Budget")
    .with("negotiate", () => "Price")
    .exhaustive();

function SmbDashboard() {
  const user = useUser();
  if (!user) {
    return null;
  }
  if (user.brandIds.length === 0) {
    return <Redirect to="/onboarding/smb" replace />;
  }
  return <SmbDashboardContent />;
}

// Exported so the admin SMB viewer (`/admin/smb/$brandId`) can render the exact
// same dashboard against an injected snapshot (see `dashboardData.tsx`). The
// route-level `SmbDashboard` gate is deliberately bypassed there — the admin
// context supplies the target brand's entity instead of the caller's own.
//
// Wraps the dashboard in `BrandSelectionProvider` so multi-brand users get a
// brand switcher and every downstream read is scoped to the selected brand. For
// a single-brand user (and the admin single-brand snapshot) the provider
// resolves to that sole brand and renders no extra chrome — the dashboard looks
// exactly as it did before multi-brand (docs/MULTI_USER_BRANDS.md §2.1).
export function SmbDashboardContent() {
  return (
    <BrandSelectionProvider>
      <SmbDashboardContentForBrand />
    </BrandSelectionProvider>
  );
}

function SmbDashboardContentForBrand() {
  const [viewMode, setViewMode] = useState<"stack" | "grid">("stack");
  const [view, setView] = useState<"active" | "completed">("active");
  const [selectedCardCategory, setSelectedCardCategory] = useState<ActionFilterCategory>("all");

  const selectedBrandId = useSelectedBrandId();

  // The dashboard views return entities/inputs across ALL the user's brands;
  // scope to the selected brand. `card_engine_input` is keyed by
  // underwritableEntityId (not brand), so resolve it through the selected
  // brand's entity ids rather than assuming a `brand` field on the row.
  const allUnderwritableEntities = useSuspendedUnderwritableEntities();
  const underwritableEntities = useMemo(
    () => allUnderwritableEntities.filter((e) => e.brand.toString() === selectedBrandId),
    [allUnderwritableEntities, selectedBrandId],
  );
  const defaultUwEntity = underwritableEntities[0];
  const defaultUwEntityId = defaultUwEntity?.id.toString();

  const allCardEngineInputs = useSuspendedCardEngineInputs();
  const entityIdsForBrand = useMemo(
    () => new Set(underwritableEntities.map((e) => e.id.toString())),
    [underwritableEntities],
  );
  const cardEngineInputs = useMemo(
    () =>
      allCardEngineInputs.filter((ci) =>
        entityIdsForBrand.has(ci.underwritableEntityId.toString()),
      ),
    [allCardEngineInputs, entityIdsForBrand],
  );
  const firstCardEngineInput = cardEngineInputs[0];

  const llmTtwComposer = useFeatureFlag("LLM_TTW_COMPOSER");
  const ttwComposer = useFeatureFlag("TTW_COMPOSER");

  const { uncompleted, completed } = useMemo<{
    uncompleted: readonly SmbActionDefinition[];
    completed: readonly SmbActionDefinition[];
  }>(() => {
    return firstCardEngineInput
      ? cardRulesEngine(firstCardEngineInput, { llmTtwComposer, ttwComposer })
      : { uncompleted: [], completed: [] };
  }, [firstCardEngineInput, llmTtwComposer, ttwComposer]);

  // Filter uncompleted by selected category before handing to the deck. The
  // controller treats this as the source of truth for "what cards exist" and
  // reconciles via its existing `actions`-effect — so swiping the filter just
  // looks like the engine emitted a different list.
  const filteredUncompleted = useMemo<readonly SmbActionDefinition[]>(() => {
    if (selectedCardCategory === "all") return uncompleted;
    return uncompleted.filter((a) => a.category === selectedCardCategory);
  }, [uncompleted, selectedCardCategory]);

  const controller = useCardDeck(filteredUncompleted);

  useLayoutEffect(() => {
    if (defaultUwEntityId === undefined) return;
    resetDocumentViewAfterSpaFormNavigation();
  }, [defaultUwEntityId]);

  // Why this is a loader rather than a throw:
  //
  // The submitUserInfo reducer commits the brand and its underwritable entity
  // in a single server transaction, but those changes reach the client as two
  // independent SpacetimeDB subscription updates: currentUser.brandIds and
  // allUnderwritableEntitiesForUser. The onboarding route redirects to
  // /profile/smb the instant currentUser updates, so this component can mount
  // before the entities row has propagated — useSuspenseQuery is settled at
  // that point (the spacetime adapter's onApplied already fired with []) and
  // does not re-suspend; subsequent inserts arrive via setQueryData and trigger
  // a normal re-render within ~100ms.
  //
  // Treating this gap as "still loading" rather than a hard error avoids
  // surfacing the root error boundary for what is really a routine inter-table
  // replication delay. If the entity genuinely never arrives, the user sees a
  // spinner instead of a fatal error — equally broken, but less alarming, and
  // the existing connection-error UX upstream will eventually surface.
  if (!defaultUwEntity) {
    return <FullPageLoader />;
  }

  return (
    <SmbDashboardContentInner
      defaultUwEntity={defaultUwEntity}
      completed={completed}
      controller={controller}
      viewMode={viewMode}
      setViewMode={setViewMode}
      view={view}
      setView={setView}
      selectedCardCategory={selectedCardCategory}
      setSelectedCardCategory={setSelectedCardCategory}
    />
  );
}

// A freshly created entity's `smb_super_view` element lands within ~100ms of
// the entity row (see the entity-gap note above); allow a generous margin for
// slow connections before treating a still-missing element as a real bug rather
// than replication lag.
const SUPER_VIEW_REPLICATION_GRACE_MS = 10_000;

type SmbDashboardContentInnerProps = {
  defaultUwEntity: { id: Uuid };
  completed: readonly SmbActionDefinition[];
  controller: ReturnType<typeof useCardDeck>;
  viewMode: "stack" | "grid";
  setViewMode: Dispatch<SetStateAction<"stack" | "grid">>;
  view: "active" | "completed";
  setView: Dispatch<SetStateAction<"active" | "completed">>;
  selectedCardCategory: ActionFilterCategory;
  setSelectedCardCategory: Dispatch<SetStateAction<ActionFilterCategory>>;
};

function SmbDashboardContentInner({
  defaultUwEntity,
  completed,
  controller,
  viewMode,
  setViewMode,
  view,
  setView,
  selectedCardCategory,
  setSelectedCardCategory,
}: SmbDashboardContentInnerProps) {
  // `smb_super_view` is a SpacetimeDB subscription independent of
  // `all_underwritable_entities_for_user`, so the element for this entity can
  // arrive a beat after the entity row that got us past the guard above. Look it
  // up without throwing and treat a miss as the same replication gap the entity
  // guard handles — but bound the wait: if the element never lands it's a real
  // bug (e.g. a brand-membership skew the super view skips), so surface the
  // diagnostic error (TYCHO-1X) instead of spinning forever and losing the signal.
  const smbSuperViewRows = useSuspendedSmbSuperViewRows();
  const smbInfo = smbSuperViewRows.find(
    (v) => v.underwritableEntityId.toString() === defaultUwEntity.id.toString(),
  );
  const superViewPending = !smbInfo;
  const [superViewGraceExpired, setSuperViewGraceExpired] = useState(false);
  useEffect(() => {
    if (!superViewPending) {
      setSuperViewGraceExpired(false);
      return;
    }
    const id = window.setTimeout(
      () => setSuperViewGraceExpired(true),
      SUPER_VIEW_REPLICATION_GRACE_MS,
    );
    return () => window.clearTimeout(id);
  }, [superViewPending]);

  if (!smbInfo) {
    if (superViewGraceExpired) {
      throw new Error("SMB super view not found for entity");
    }
    return <FullPageLoader />;
  }

  // Active and completed views stay mounted side-by-side. Toggling between
  // them hides the inactive one via `inert` + opacity rather than unmounting,
  // so the deck never remounts mid-animation (Motion's `layout` snapshot
  // timing was the source of the v1 stack-collapse bug).
  const isActive = view === "active";

  const hasAccountingConnection = smbInfo.connections.some((c) => c.kind === "accounting");

  return (
    <AcknowledgmentsProvider
      underwritableEntityId={defaultUwEntity.id}
      acknowledgments={smbInfo.userUwAcknowledgments}
    >
      <OrientationTour onPickCategory={setSelectedCardCategory}>
        <QuickbooksAcknowledgementModal when={hasAccountingConnection} />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex h-dvh flex-col"
          // Dev-only seam: the e2e walkthroughs resolve the onboarded brand +
          // underwritable-entity ids from here (to brand-scope Athena-mock
          // scenarios and target the dashboard-bypass reducers) instead of
          // parsing UUIDs out of `spacetime sql`. Rendered only in dev; these
          // are the viewer's own ids, not secrets.
          data-e2e-brand-id={env.VITE_TYCHO_ENV === "dev" ? smbInfo.brand.id.toString() : undefined}
          data-e2e-uw-entity-id={
            env.VITE_TYCHO_ENV === "dev" ? defaultUwEntity.id.toString() : undefined
          }
        >
          <DefaultNavbar />
          <div className="mx-auto flex w-full max-w-[1500px] flex-1 flex-col overflow-x-clip px-4 pt-2 pb-20 md:px-8 md:pt-6 md:pb-28">
            <header className="mx-auto mb-4 flex w-full items-center justify-between">
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-3">
                  <h1 className="withus-strong text-2xl">Plan</h1>
                  {/* Renders only for multi-brand users; single-brand header is unchanged. */}
                  <BrandSwitcher />
                </div>
                <Button
                  variant="link"
                  className="text-muted-foreground uppercase"
                  onClick={() => setView((v) => (v === "active" ? "completed" : "active"))}
                >
                  {isActive ? `${completed.length} complete` : "Show to-do"}
                </Button>
              </div>
              <div
                inert={!isActive}
                className={cn(
                  "flex shrink-0 items-center gap-2",
                  !isActive && "opacity-0 **:transition-none",
                )}
              >
                <CategoryFilterSelect
                  value={selectedCardCategory}
                  onValueChange={setSelectedCardCategory}
                />
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(v) => v !== "" && setViewMode(v as "stack" | "grid")}
                  className="rounded-none border border-black"
                >
                  <ToggleGroupItem value="stack" aria-label="Stack view">
                    <LayersIcon />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="grid" aria-label="Grid view">
                    <LayoutGridIcon />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </header>

            <div className="relative flex min-h-0 flex-1 flex-col">
              <div
                inert={!isActive}
                aria-hidden={!isActive}
                className={cn(
                  "flex min-h-0 flex-1 flex-col transition-opacity duration-200",
                  !isActive && "pointer-events-none absolute inset-0 opacity-0",
                )}
              >
                {controller.cards.length === 0 ? (
                  <div className="border border-border p-8 text-center text-sm text-muted-foreground">
                    {selectedCardCategory === "all"
                      ? "You're all caught up."
                      : `No ${humanCardCategory(selectedCardCategory).toLowerCase()} cards right now.`}
                  </div>
                ) : (
                  <CardDeck controller={controller} viewMode={viewMode} />
                )}
              </div>

              <div
                inert={isActive}
                aria-hidden={isActive}
                className={cn(
                  "flex min-h-0 flex-1 flex-col transition-opacity duration-200",
                  isActive && "pointer-events-none absolute inset-0 opacity-0",
                )}
              >
                {completed.length === 0 ? (
                  <div className="border border-border p-8 text-center text-sm text-muted-foreground">
                    Nothing completed yet.
                  </div>
                ) : (
                  <section className="mx-auto w-full max-w-3xl">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                      {completed.map((action) => (
                        <div key={action.id}>{action.render({ onComplete: () => {} })}</div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>

          <footer
            className={cn(
              "fixed inset-x-0 bottom-0 z-30 border-t border-border bg-withus-pure-white px-4 pt-4 pb-4",
              "md:inset-x-auto md:bottom-8 md:left-1/2 md:w-full md:max-w-100 md:-translate-x-1/2 md:border md:border-border",
              "shadow-withus-default",
            )}
          >
            <div className="grid grid-cols-3 gap-6 divide-x divide-border">
              <FundraiseTileButton underwritableEntityId={defaultUwEntity.id} />
              <BudgetTileButton underwritableEntityId={defaultUwEntity.id} />
              <NegotiateTileButton underwritableEntityId={defaultUwEntity.id} />
            </div>
          </footer>
        </motion.div>
      </OrientationTour>
    </AcknowledgmentsProvider>
  );
}

// Pulled into its own component so `useTourStep` runs underneath
// `<OrientationTour>`'s provider — calling it directly in
// `SmbDashboardContentInner` would land outside that subtree and silently
// no-op.
function CategoryFilterSelect({
  value,
  onValueChange,
}: {
  value: ActionFilterCategory;
  onValueChange: (v: ActionFilterCategory) => void;
}) {
  const tourRef = useTourStep("categoryFilter");
  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as ActionFilterCategory)}>
      <SelectTrigger ref={tourRef}>
        <SelectValue>{humanCardCategory(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {FILTERABLE_CATEGORIES.map((category) => (
          <SelectItem key={category} value={category}>
            {humanCardCategory(category)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function QuickbooksAcknowledgementModal({ when }: { when: boolean }) {
  const isDesktop = useDrawerSmUp();
  return (
    <AcknowledgmentModal
      messageKey={MESSAGE_KEYS.quickbooksConnected}
      when={when}
      title="Nice!"
      body={
        <div className="space-y-8">
          <p>
            You've connected your QuickBooks account, and we've started processing your financial
            data. We may have some follow-up questions for you; they'll show up in your deck of
            cards if answers are required.
          </p>
          <Timeline value={1} orientation={isDesktop ? "horizontal" : "vertical"}>
            <TimelineItem step={0}>
              <TimelineSeparator className="bg-withus-blue!" />
              <TimelineIndicator className="size-5 border-withus-blue! bg-withus-blue!">
                <CheckIcon className="size-4 text-white" />
              </TimelineIndicator>
              <TimelineTitle>Onboarding complete</TimelineTitle>
            </TimelineItem>
            <TimelineItem step={1}>
              <TimelineSeparator />
              <TimelineIndicator className="size-5 bg-withus-blue group-data-completed/timeline-item:border-withus-blue">
                <CheckIcon className="size-4 text-white" />
              </TimelineIndicator>
              <TimelineTitle>QuickBooks connected</TimelineTitle>
            </TimelineItem>
            <TimelineItem step={2}>
              <TimelineSeparator />
              <TimelineIndicator className="size-5" />
              <TimelineTitle>Follow-up questions answered</TimelineTitle>
            </TimelineItem>
            <TimelineItem step={3}>
              <TimelineIndicator className="size-5" />
              <TimelineTitle>Max affordabilty calculated</TimelineTitle>
            </TimelineItem>
          </Timeline>
          <p>
            In the meantime, you can connect additional accounts to give us more financial context,
            or check out other cards related to fundraising or negotiation.
          </p>
        </div>
      }
    />
  );
}
